import React, { createContext, useContext, useState, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';

/* ── CSS-variable references (use in styled-components) ── */

export const c = {
  bg: 'var(--w-bg)',
  bgAlt: 'var(--w-bg-alt)',
  bgWhite: 'var(--w-bg-white)',
  text: 'var(--w-text)',
  textSec: 'var(--w-text-sec)',
  textMuted: 'var(--w-text-muted)',
  accent: 'var(--w-accent)',
  accentHover: 'var(--w-accent-hover)',
  accentSoft: 'var(--w-accent-soft)',
  emColor: 'var(--w-em-color)',
  emBg: 'var(--w-em-bg)',
  citation: 'var(--w-citation)',
  citationSoft: 'var(--w-citation-soft)',
  verified: 'var(--w-verified)',
  verifiedSoft: 'var(--w-verified-soft)',
  border: 'var(--w-border)',
  borderLight: 'var(--w-border-light)',
  shadow: 'var(--w-shadow)',
  shadowMd: 'var(--w-shadow-md)',
  overlay: 'var(--w-overlay)',
  headerBg: 'var(--w-header-bg)',
};

/* ── Non-color constants ─────────────────────────────── */

export const wiki = {
  fonts: {
    heading: "'Playfair Display', Georgia, 'Times New Roman', serif",
    body: "'Source Serif 4', 'Source Serif Pro', Georgia, serif",
    ui: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    mono: "'Consolas', 'Monaco', monospace",
  },
  maxContent: '720px',
  maxPage: '1100px',
  headerHeight: '60px',
  tocWidth: '220px',
  radius: '8px',
  radiusSm: '4px',
};

/* ── Color helpers ────────────────────────────────────── */

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

function darken(hex, amt = 0.15) {
  const [r, g, b] = hexToRgb(hex);
  const f = 1 - amt;
  const hx = (v) => Math.round(v * f).toString(16).padStart(2, '0');
  return `#${hx(r)}${hx(g)}${hx(b)}`;
}

function lighten(hex, amt = 0.2) {
  const [r, g, b] = hexToRgb(hex);
  const hx = (v) =>
    Math.round(v + (255 - v) * amt)
      .toString(16)
      .padStart(2, '0');
  return `#${hx(r)}${hx(g)}${hx(b)}`;
}

export function buildThemeVars(primaryColor = '#C4841D', mode = 'light') {
  const accent = primaryColor;
  const [ar, ag, ab] = hexToRgb(accent);

  if (mode === 'dark') {
    const al = lighten(accent, 0.18);
    const [lr, lg, lb] = hexToRgb(al);
    return {
      '--w-bg': '#1A1816',
      '--w-bg-alt': '#222019',
      '--w-bg-white': '#2A2722',
      '--w-text': '#E8E0D4',
      '--w-text-sec': '#C4BAA8',
      '--w-text-muted': '#9A9189',
      '--w-accent': al,
      '--w-accent-hover': lighten(accent, 0.3),
      '--w-accent-soft': `rgba(${lr}, ${lg}, ${lb}, 0.12)`,
      '--w-em-color': lighten(accent, 0.25),
      '--w-em-bg': `rgba(${lr}, ${lg}, ${lb}, 0.10)`,
      '--w-citation': `rgb(${lr}, ${lg}, ${lb})`,
      '--w-citation-soft': `rgba(${lr}, ${lg}, ${lb}, 0.10)`,
      '--w-verified': '#8FA87E',
      '--w-verified-soft': 'rgba(143, 168, 126, 0.12)',
      '--w-border': '#3A3630',
      '--w-border-light': '#302C27',
      '--w-shadow': 'rgba(0, 0, 0, 0.25)',
      '--w-shadow-md': 'rgba(0, 0, 0, 0.40)',
      '--w-overlay': 'rgba(0, 0, 0, 0.60)',
      '--w-header-bg': 'rgba(26, 24, 22, 0.96)',
    };
  }

  return {
    '--w-bg': '#FAF6F0',
    '--w-bg-alt': '#F5F0E8',
    '--w-bg-white': '#FFFFFF',
    '--w-text': '#2D2A26',
    '--w-text-sec': '#5C5650',
    '--w-text-muted': '#8A8279',
    '--w-accent': accent,
    '--w-accent-hover': darken(accent),
    '--w-accent-soft': `rgba(${ar}, ${ag}, ${ab}, 0.08)`,
    '--w-em-color': darken(accent, 0.2),
    '--w-em-bg': `rgba(${ar}, ${ag}, ${ab}, 0.07)`,
    '--w-citation': accent,
    '--w-citation-soft': `rgba(${ar}, ${ag}, ${ab}, 0.08)`,
    '--w-verified': '#6B7F5E',
    '--w-verified-soft': 'rgba(107, 127, 94, 0.1)',
    '--w-border': '#E8E0D4',
    '--w-border-light': '#F0EBE3',
    '--w-shadow': 'rgba(45, 42, 38, 0.06)',
    '--w-shadow-md': 'rgba(45, 42, 38, 0.12)',
    '--w-overlay': 'rgba(45, 42, 38, 0.50)',
    '--w-header-bg': 'rgba(253, 251, 248, 0.96)',
  };
}

/* ── Theme context ───────────────────────────────────── */

export const COLOR_PRESETS = [
  { hex: '#EBB21B', name: 'Gold' },
  { hex: '#539471', name: 'Sage' },
  { hex: '#5C5194', name: 'Violet' },
  { hex: '#C54C63', name: 'Rose' },
  { hex: '#4c9fc5', name: 'Sky' },
];

const WikiThemeContext = createContext({
  mode: 'light',
  toggleMode: () => {},
  accentColor: '#C4841D',
  setAccentColor: () => {},
});

export function useWikiTheme() {
  return useContext(WikiThemeContext);
}

export function WikiThemeProvider({ primaryColor, children }) {
  const [mode, setMode] = useState(
    () => localStorage.getItem('wiki_theme') || 'light',
  );

  const [accentColor, setAccentColorState] = useState(
    () => localStorage.getItem('wiki_accent') || '',
  );

  const effectiveColor = accentColor || primaryColor || '#C4841D';

  const toggleMode = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('wiki_theme', next);
      return next;
    });
  };

  const setAccentColor = (hex) => {
    if (hex) {
      localStorage.setItem('wiki_accent', hex);
    } else {
      localStorage.removeItem('wiki_accent');
    }
    setAccentColorState(hex || '');
  };

  const vars = useMemo(
    () => buildThemeVars(effectiveColor, mode),
    [effectiveColor, mode],
  );

  return (
    <WikiThemeContext.Provider
      value={{ mode, toggleMode, accentColor: effectiveColor, setAccentColor }}
    >
      <div style={vars}>{children}</div>
    </WikiThemeContext.Provider>
  );
}

/* ── Keyframes ────────────────────────────────────────── */

export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

/* ── Base components ─────────────────────────────────── */

export const WikiContainer = styled.div`
  min-height: 100vh;
  background: ${c.bg};
  color: ${c.text};
  font-family: ${wiki.fonts.body};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  transition: background 0.3s ease, color 0.3s ease;
`;

export const WikiInner = styled.div`
  max-width: ${wiki.maxPage};
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1.25rem;
  }
`;

export const SkeletonLine = styled.div`
  background: linear-gradient(
    90deg,
    ${c.borderLight} 25%,
    ${c.border} 50%,
    ${c.borderLight} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: ${wiki.radiusSm};
  height: ${(p) => p.$h || '1rem'};
  width: ${(p) => p.$w || '100%'};
  margin-bottom: ${(p) => p.$mb || '0.75rem'};
`;
